import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextField,
  Button,
  Typography,
  IconButton,
  Fade,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import ListIcon from '@material-ui/icons/List';
import { useStyles } from './ManageTradeTypesModalStyles';

interface TradeType {
  name: string;
  shortName: string;
  isDefault: boolean;
}

interface ManageTradeTypesModalProps {
  open: boolean;
  onClose: () => void;
  tradeTypes: TradeType[];
  onSave: (tradeTypes: TradeType[]) => void;
}

export default function ManageTradeTypesModal({
  open,
  onClose,
  tradeTypes,
  onSave,
}: ManageTradeTypesModalProps) {
  const classes = useStyles();
  const [localTradeTypes, setLocalTradeTypes] = useState<TradeType[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (open && !isInitialized) {
      const initializedTypes = Array.isArray(tradeTypes) && tradeTypes.length > 0
        ? tradeTypes.map(type => ({
            name: String(type.name || ''),
            shortName: String(type.shortName || ''),
            isDefault: Boolean(type.isDefault),
          }))
        : [];
      setLocalTradeTypes(initializedTypes);
      setIsInitialized(true);
      setErrorMessage('');
    } else if (!open) {
      setIsInitialized(false);
      setErrorMessage('');
    }
  }, [open, tradeTypes, isInitialized]);

  const handleNameChange = (index: number, value: string) => {
    const updated = [...localTradeTypes];
    updated[index] = { ...updated[index], name: value };
    setLocalTradeTypes(updated);
    if (errorMessage) setErrorMessage('');
  };

  const handleShortNameChange = (index: number, value: string) => {
    const updated = [...localTradeTypes];
    updated[index] = { ...updated[index], shortName: value };
    setLocalTradeTypes(updated);
    if (errorMessage) setErrorMessage('');
  };

  const handleSetDefault = (index: number) => {
    const updated = localTradeTypes.map((tradeType, i) => ({
      ...tradeType,
      isDefault: i === index,
    }));
    setLocalTradeTypes(updated);
  };

  const handleDelete = (index: number) => {
    const updated = localTradeTypes.filter((_, i) => i !== index);
    setLocalTradeTypes(updated);
  };

  const handleAddNew = () => {
    const newTradeType: TradeType = {
      name: '',
      shortName: '',
      isDefault: localTradeTypes.length === 0,
    };
    setLocalTradeTypes([...localTradeTypes, newTradeType]);
  };

  const handleSave = () => {
    const isValid = localTradeTypes.every(
      tradeType => tradeType.name.trim() && tradeType.shortName.trim()
    );

    if (!isValid) {
      setErrorMessage('Preencha o nome e a sigla de todos os tipos.');
      return;
    }

    const hasDefault = localTradeTypes.some(tradeType => tradeType.isDefault);
    if (!hasDefault && localTradeTypes.length > 0) {
      const updated = [...localTradeTypes];
      updated[0] = { ...updated[0], isDefault: true };
      setLocalTradeTypes(updated);
      onSave(updated);
    } else {
      onSave(localTradeTypes);
    }
    onClose();
  };

  const handleCancel = () => {
    setLocalTradeTypes([...tradeTypes]);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      className={classes.modal}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <div className={classes.header}>
            <div>
              <Typography className={classes.title}>
                Gerenciar Tipos de Trade
              </Typography>
              <Typography className={classes.subtitle}>
                {localTradeTypes.length} tipo{localTradeTypes.length !== 1 ? 's' : ''} cadastrado{localTradeTypes.length !== 1 ? 's' : ''}
              </Typography>
            </div>
            <IconButton className={classes.closeButton} onClick={handleCancel} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div className={classes.itemList}>
            {localTradeTypes.length === 0 ? (
              <div className={classes.emptyState}>
                <ListIcon className={classes.emptyIcon} />
                <Typography className={classes.emptyText}>
                  Nenhum tipo de trade cadastrado.<br />
                  Adicione um novo tipo para comecar.
                </Typography>
              </div>
            ) : (
              localTradeTypes.map((tradeType, index) => (
                <div key={index} className={classes.tradeTypeRow}>
                  <span className={classes.rowIndex}>{index + 1}</span>
                  <TextField
                    className={classes.input}
                    label="Nome"
                    value={tradeType.name}
                    onChange={(e) => handleNameChange(index, e.target.value)}
                    variant="outlined"
                    size="small"
                    placeholder="Ex: Day Trade"
                  />
                  <TextField
                    className={`${classes.input} ${classes.shortInput}`}
                    label="Sigla"
                    value={tradeType.shortName}
                    onChange={(e) => handleShortNameChange(index, e.target.value)}
                    variant="outlined"
                    size="small"
                    placeholder="Ex: DT"
                    inputProps={{ maxLength: 6 }}
                  />
                  <IconButton
                    className={tradeType.isDefault ? classes.defaultButtonActive : classes.defaultButton}
                    onClick={() => handleSetDefault(index)}
                    size="small"
                    title={tradeType.isDefault ? 'Tipo padrao' : 'Definir como padrao'}
                  >
                    {tradeType.isDefault ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                  </IconButton>
                  <IconButton
                    className={classes.deleteButton}
                    onClick={() => handleDelete(index)}
                    size="small"
                    title="Remover tipo"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              ))
            )}
          </div>

          {errorMessage && (
            <Typography className={classes.errorMessage}>
              {errorMessage}
            </Typography>
          )}

          <Button
            className={classes.addButton}
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Adicionar novo tipo
          </Button>

          <div className={classes.actionButtons}>
            <Button
              onClick={handleCancel}
              variant="outlined"
              className={classes.cancelButton}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              className={classes.saveButton}
              disabled={localTradeTypes.length === 0}
            >
              Salvar
            </Button>
          </div>
        </div>
      </Fade>
    </Modal>
  );
}
